import { type PracticeTabSelection } from "../../components/practice-feed/PracticeFeedRail";
import { type PracticeRailCorpus } from "../../domains/practice/hooks/usePracticeRailTerms";

/**
 * What the URL asks for, before the reader's catalogue has had a say.
 *
 * THREE parameters, and the practice is now one of them in its own right rather
 * than one member of the chip selection: `?practice=<uid>` is the CORPUS,
 * `&mesh=<descriptorUi>` narrows it, `&room=<id>` leaves it for an editorial one.
 * `?room=` is kept spelled exactly as the other arm spells it, so a shared room
 * link works identically in both.
 *
 * A `?topic=` left over from the other arm lands on "nothing pinned" rather than
 * on an error: the two arms share one path, so a bookmark made in one of them
 * will be opened in the other the day a reader is re-bucketed, and the honest
 * reading of a filter this arm cannot serve is "no filter".
 *
 * `mesh` and `room` cannot both be honoured, and `room` wins: it names a corpus,
 * while a descriptor only narrows one. A URL carrying both is not something this
 * page ever writes.
 */
/**
 * What the page is READING, as one value.
 *
 * A union, and not a chip selection sitting beside a bag of filters: a Room takes
 * NO filters. It is a different corpus, curated and ranked by its partner, and
 * neither this practice's descriptors nor its journals say anything about it.
 * Written this way, "a Room with two journals ticked" cannot be expressed.
 *
 * It is also why the rail's `selection` is derived from this rather than being
 * this: `selection: null` used to mean "nothing filtered", and the moment the
 * drawer's axes existed it stopped being true — no chip lit with two journals on
 * is an ordinary state.
 */
export type PracticeFeedMode =
  | { kind: "room"; roomId: string }
  | {
      kind: "practice";
      /** The rail's live chip, or `null` for the whole practice. */
      descriptorUi: string | null;
      /** The drawer's two axes, both multi-select. Empty is "not filtering". */
      journalUids: readonly string[];
      publicationTypes: readonly string[];
    };

/** The unfiltered practice: a real destination, and its ONLY spelling. */
export const WHOLE_PRACTICE: PracticeFeedMode = {
  kind: "practice",
  descriptorUi: null,
  journalUids: [],
  publicationTypes: [],
};

/**
 * How the aggregate spells itself in the URL.
 *
 * A profile uid is a kebab slug minted by the catalogue generator, and `all` is
 * not a practice, so the two namespaces cannot meet. `/me` next door reserves a
 * literal at the same position for the same reason. If a catalogue ever mints a
 * practice called `all` the aggregate shadows it, which is a catalogue bug this
 * page is the wrong place to defend against.
 */
const ALL_PRACTICES_PARAM = "all";

type PracticeFeedRequest = {
  /** `null` when the URL pins none, which resolves to `defaultView`. */
  corpus: PracticeRailCorpus | null;
  mode: PracticeFeedMode;
};

// Exported for the round-trip test next door, with `buildSelectionSearch`. They
// are two halves of one encoding and have to agree — which stopped being obvious
// the moment an axis started writing its key more than once.
export function readSelectionFromSearch(search: string): PracticeFeedRequest {
  const params = new URLSearchParams(search);
  const practice = params.get("practice");
  const corpus: PracticeRailCorpus | null =
    practice === null || practice === ""
      ? null
      : practice === ALL_PRACTICES_PARAM
        ? { kind: "all" }
        : { kind: "practice", uid: practice };

  const room = params.get("room");
  if (room !== null && room !== "")
    return { corpus, mode: { kind: "room", roomId: room } };

  const mesh = params.get("mesh");
  return {
    corpus,
    mode: {
      kind: "practice",
      descriptorUi: mesh === null || mesh === "" ? null : mesh,
      // Repeated keys (`?journal=a&journal=b`), which is what a multi-select axis
      // is on a URL. An empty value is dropped rather than sent as a filter that
      // matches nothing.
      journalUids: params.getAll("journal").filter((value) => value !== ""),
      publicationTypes: params.getAll("type").filter((value) => value !== ""),
    },
  };
}

/**
 * What the page is actually reading, once the catalogue has had its say.
 *
 * Identical to {@link PracticeFeedRequest} but for `corpus`, which is no longer
 * nullable — turning a request into a fact is the whole job of
 * `resolvePracticeSelection`, so the two shapes differ by exactly the thing it
 * decides. The corpus is ALWAYS present, including in Room mode: a Room is read
 * from inside a corpus's surface (its chip lives in that rail, the header keeps
 * naming what you return to), and the alternative — a Room with no corpus — would
 * leave the header with nothing to say and the reader with no way back.
 */
export type PracticeFeedView = {
  corpus: PracticeRailCorpus;
  mode: PracticeFeedMode;
};

/**
 * The selection the URL asks for, reconciled with the catalogue the reader
 * actually holds — and the place the DEFAULT practice is decided.
 *
 * A `?practice=` uid is a request, not a fact. The publications endpoint serves
 * any active profile by uid — it is not scoped to what the reader holds — so
 * unchecking the selected practice in the sheet used to leave the feed serving it
 * with no chip highlighted: the save HAD refreshed both queries, and the surface
 * still showed a practice that was no longer anyone's. Same for a stale bookmark,
 * and for a profile the medical team retires. Both fall back to `defaultView`,
 * which is also what a bare `/discovery` opens on: ALL practices for a reader
 * holding more than one, the practice itself for a reader holding one.
 *
 * Nothing is persisted: the default is re-derived on every render from the
 * catalogue the reader holds, so adding a second practice in the sheet moves a
 * bare `/discovery` onto the aggregate with no other state to update. There is
 * no notion of a principal practice, and inventing one from corpus size or from
 * the catalogue's review order would be the surface deciding what someone
 * mostly does.
 *
 * ── No FILTER survives a fallback ───────────────────────────────────────────
 * A descriptor is a chip of ONE practice's rail, and a journal is a row of ONE
 * practice's drawer — both discovered from the practice that is going away.
 * Carried onto a different one they would filter the feed from nowhere: nothing
 * lit, the pinned counter reading 3, and a drawer that cannot name what is on. So
 * a practice that changes under the request opens WHOLE, which is exactly what
 * changing practice by hand does, and the exact symmetry of "Clear all never
 * clears the practice".
 *
 * A ROOM survives, because it is not a filter of anything: it is an editorial
 * corpus whose chip means the same thing whichever practice the header names, so
 * a shared room link keeps working for a reader whose `?practice=` went stale.
 *
 * `null` OUT means nothing is readable yet: the catalogue has not landed and the
 * URL pinned nothing, or it landed empty. The first is a frame or two — the page
 * is mounted by `DiscoverySurface`, which has already read this exact query — and
 * the second unmounts the page. Both render as the feed's own pending state,
 * never as an empty corpus.
 *
 * `catalogue === null` still trusts a `?practice=` deep link: falling back while
 * the list is merely unread would throw away every deep link on a cold load,
 * which is the same bug facing the other way.
 */
export function resolvePracticeSelection(args: {
  requested: PracticeFeedRequest;
  catalogue: readonly { uid: string }[] | null;
}): PracticeFeedView | null {
  const { requested, catalogue } = args;
  const requestedCorpus = requested.corpus;
  if (requestedCorpus === null)
    return catalogue === null ? null : defaultView(catalogue, requested.mode);

  // "All practices" is not a uid that can go stale, so it resolves before the
  // catalogue lands, which is what makes a shared `?practice=all` link open on
  // the right corpus rather than waiting a frame in the pending state like a
  // bare URL does. Once the catalogue is read it can still be the WRONG corpus:
  // a reader who trimmed their practices down to one is left with the header
  // naming an aggregate the dropdown no longer offers, so it falls back to
  // `defaultView` like a stale uid does, and the one practice is what that is.
  if (requestedCorpus.kind === "all")
    return catalogue !== null && catalogue.length <= 1
      ? defaultView(catalogue, requested.mode)
      : { corpus: requestedCorpus, mode: aggregateMode(requested.mode) };

  if (catalogue === null)
    return { corpus: requestedCorpus, mode: requested.mode };

  if (catalogue.some((entry) => entry.uid === requestedCorpus.uid))
    return { corpus: requestedCorpus, mode: requested.mode };

  return defaultView(catalogue, requested.mode);
}

/** What a bare `/discovery` opens on, and the fallback destination. */
function defaultView(
  catalogue: readonly { uid: string }[],
  mode: PracticeFeedMode
): PracticeFeedView | null {
  const first = catalogue.at(0);
  if (first === undefined) return null;
  const whole = mode.kind === "room" ? mode : WHOLE_PRACTICE;
  // The dropdown offers the aggregate only to a reader holding more than one
  // (`PracticeDropdown`), so the default lands where the menu can lead back
  // from. With one practice the aggregate IS that practice under another name,
  // with no drawer and no JuisciGPT hand-off.
  return catalogue.length > 1
    ? { corpus: { kind: "all" }, mode: whole }
    : { corpus: { kind: "practice", uid: first.uid }, mode: whole };
}

/**
 * The aggregate carries a chip but NO drawer axis, and that is a fact about the
 * API rather than a choice made here: journal and publication-type values are
 * counted over one practice's matched set, and nothing counts them over a union.
 * Ticking a value the drawer cannot list or clear is the dead end this design
 * refuses, so the URL cannot express one.
 */
function aggregateMode(mode: PracticeFeedMode): PracticeFeedMode {
  if (mode.kind === "room") return mode;
  return {
    kind: "practice",
    descriptorUi: mode.descriptorUi,
    journalUids: [],
    publicationTypes: [],
  };
}

// Always explicit, including for the default corpus and for the unfiltered
// feed: the empty search is a valid ENTRY (a bare `/discovery`, a stale
// `?topic=`) but never something this page writes, so one state cannot end up
// under two URLs — and with it two `scrollPathname` keys for one feed.
export function buildSelectionSearch(view: PracticeFeedView): string {
  const params = new URLSearchParams();
  params.set(
    "practice",
    view.corpus.kind === "all" ? ALL_PRACTICES_PARAM : view.corpus.uid
  );
  switch (view.mode.kind) {
    case "room":
      params.set("room", view.mode.roomId);
      break;
    case "practice":
      if (view.mode.descriptorUi !== null)
        params.set("mesh", view.mode.descriptorUi);
      // `append`, not `set`: these axes are multi-select, so the URL carries the
      // key once per ticked value. In the written order, which is the order the
      // drawer listed them — so re-ticking the same boxes produces the same URL
      // and not a second `scrollPathname` key for one feed.
      for (const uid of view.mode.journalUids) params.append("journal", uid);
      for (const type of view.mode.publicationTypes)
        params.append("type", type);
      break;
    default:
      throw new Error(view.mode satisfies never);
  }
  return `?${params.toString()}`;
}

/**
 * Which chip the rail lights, which is a VIEW of the mode and not the mode.
 *
 * `null` means no chip is lit, and it no longer means "nothing is filtered": the
 * drawer's two axes never appear on the row, so an unlit rail above a feed
 * narrowed to two journals is an ordinary state. The pinned chip's badge is what
 * shows those.
 */
export function litChip(mode: PracticeFeedMode): PracticeTabSelection | null {
  switch (mode.kind) {
    case "room":
      return { kind: "room", roomId: mode.roomId };
    case "practice":
      return mode.descriptorUi === null
        ? null
        : { kind: "mesh", descriptorUi: mode.descriptorUi };
    default:
      throw new Error(mode satisfies never);
  }
}

/**
 * A chip tap, resolved against what is already on.
 *
 * ── Selecting a term clears the two axes; clearing one keeps them ───────────
 * Not an arbitrary asymmetry. The drawer's values are counted INSIDE the live
 * chip, so narrowing can drop a value that is still ticked — leaving a filter the
 * panel can neither show nor remove, which is the dead end this whole design
 * refuses. Widening cannot do that: every value of the narrow view is in the wide
 * one, with a count that can only have grown. So the direction that can strand a
 * filter is the direction that clears them.
 */
export function modeForChip(
  current: PracticeFeedMode,
  next: PracticeTabSelection | null
): PracticeFeedMode {
  if (next === null) {
    if (current.kind === "room") return WHOLE_PRACTICE;
    return {
      kind: "practice",
      descriptorUi: null,
      journalUids: current.journalUids,
      publicationTypes: current.publicationTypes,
    };
  }
  switch (next.kind) {
    case "room":
      return { kind: "room", roomId: next.roomId };
    case "mesh":
      return {
        kind: "practice",
        descriptorUi: next.descriptorUi,
        journalUids: [],
        publicationTypes: [],
      };
    default:
      throw new Error(next satisfies never);
  }
}
